import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getPlatformDomains } from '../../lib/api';
import { Steps } from './WorkspaceName';
import { Globe, CheckCircle, XCircle, ArrowRight, Star } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const LINKFLOW_DOMAIN = 'linkflow.link';
const RESERVED = ['admin', 'api', 'app', 'www', 'mail', 'help', 'support', 'billing', 'dashboard', 'linkflow', 'login'];

function slugify(name = '') {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);
}

export default function ClaimSubdomain() {
  const { workspace, setWorkspace } = useAuth();
  const navigate = useNavigate();

  // --- Platform domains (lfo.to, gfo.to, cto.to) ---
  const [platformDomains, setPlatformDomains] = useState([]);
  const [selectedPlatformDomain, setSelectedPlatformDomain] = useState('');

  // --- Free subdomain (linkflow.link) ---
  const [subdomain, setSubdomain] = useState('');
  const [available, setAvailable] = useState(null);
  const [checking, setChecking] = useState(false);

  // --- Mode: 'platform' | 'subdomain' | 'custom' ---
  const [mode, setMode] = useState('platform');
  const [claiming, setClaiming] = useState(false);

  // ── Populate subdomain from workspace name once workspace loads ───────────
  useEffect(() => {
    if (workspace?.name && !subdomain) {
      setSubdomain(slugify(workspace.name));
    }
  }, [workspace?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load platform domains ─────────────────────────────────────────────────
  useEffect(() => {
    getPlatformDomains()
      .then((domains) => {
        setPlatformDomains(domains);
        if (domains.length > 0 && !selectedPlatformDomain) {
          setSelectedPlatformDomain(domains[0].domain);
        }
      })
      .catch(() => {
        // Fallback hardcoded list so the page never gets stuck
        const fallback = [
          { domain: 'lfo.to', label: 'lfo.to' },
          { domain: 'gfo.to', label: 'gfo.to' },
          { domain: 'cto.to', label: 'cto.to' },
        ];
        setPlatformDomains(fallback);
        setSelectedPlatformDomain('lfo.to');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Availability check for linkflow.link subdomain ────────────────────────
  useEffect(() => {
    if (mode !== 'subdomain') return;
    if (!subdomain || subdomain.length < 3) { setAvailable(null); return; }
    if (RESERVED.includes(subdomain)) { setAvailable(false); return; }

    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('workspaces')
          .select('workspace_id')
          .eq('subdomain', subdomain)
          .neq('workspace_id', workspace?.workspace_id || 'none')
          .maybeSingle();
        setAvailable(!data);
      } catch {
        setAvailable(true); // no row = available
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [subdomain, mode, workspace?.workspace_id]);

  // ── Claim platform domain (just saves preference to workspace) ────────────
  const handleClaimPlatform = async () => {
    if (!selectedPlatformDomain) return;
    try {
      setClaiming(true);
      // Store chosen platform domain in workspace settings
      const { data, error } = await supabase
        .from('workspaces')
        .update({
          default_short_domain: selectedPlatformDomain,
          onboarding: {
            ...(workspace?.onboarding || {}),
            connected_domain: true,
          },
        })
        .eq('workspace_id', workspace.workspace_id)
        .select()
        .single();
      if (error) throw error;
      setWorkspace(data);
      toast.success(`${selectedPlatformDomain} set as your default short domain`);
      navigate('/onboarding/ready');
    } catch (err) {
      toast.error(err.message || 'Failed to save domain preference');
    } finally {
      setClaiming(false);
    }
  };

  // ── Claim linkflow.link subdomain ─────────────────────────────────────────
  const handleClaimSubdomain = async () => {
    if (!subdomain || available !== true) return;
    try {
      setClaiming(true);
      const { data, error } = await supabase
        .from('workspaces')
        .update({
          subdomain,
          onboarding: {
            ...(workspace?.onboarding || {}),
            connected_domain: true,
          },
        })
        .eq('workspace_id', workspace.workspace_id)
        .select()
        .single();
      if (error) throw error;
      setWorkspace(data);
      toast.success(`${subdomain}.${LINKFLOW_DOMAIN} claimed!`);
      navigate('/onboarding/ready');
    } catch (err) {
      toast.error(err.message || 'Failed to claim subdomain');
    } finally {
      setClaiming(false);
    }
  };

  const handleSkip = () => navigate('/onboarding/ready');

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <Steps current={1} />
      </div>

      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-soft">
            <Globe size={28} weight="fill" />
          </span>
        </div>

        <h1 className="text-3xl font-extrabold text-center text-foreground mb-2">
          Choose your short domain
        </h1>
        <p className="text-center text-muted-foreground mb-8">
          Pick a short domain for your links. You can always change this later in Settings.
        </p>

        {/* Mode tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6">
          {[
            { id: 'platform',  label: '⚡ Short domains' },
            { id: 'subdomain', label: 'Free subdomain' },
            { id: 'custom',    label: 'Custom domain' },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                mode === m.id
                  ? 'bg-white text-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* ── Platform short domains ── */}
        {mode === 'platform' && (
          <div className="bg-white rounded-2xl border border-border shadow-soft p-8 space-y-5">
            <div className="flex items-center gap-2 mb-1">
              <Star size={14} weight="fill" className="text-amber-400" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Recommended — shared short domains, clean slugs
              </p>
            </div>

            <div className="space-y-3">
              {platformDomains.length === 0 ? (
                /* Loading skeleton */
                [1, 2, 3].map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-muted/50 animate-pulse" />
                ))
              ) : (
                platformDomains.map((d) => (
                  <button
                    key={d.domain}
                    onClick={() => setSelectedPlatformDomain(d.domain)}
                    className={`w-full flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all ${
                      selectedPlatformDomain === d.domain
                        ? 'border-primary bg-primary/[0.04] ring-2 ring-primary/20'
                        : 'border-border hover:border-primary/40 hover:bg-muted/30'
                    }`}
                  >
                    <div>
                      <p className="font-mono text-base font-bold text-foreground">{d.domain}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        e.g.{' '}
                        <span className="font-mono">
                          {d.domain}/{slugify(workspace?.name || 'my-link')}
                        </span>
                      </p>
                    </div>
                    {selectedPlatformDomain === d.domain && (
                      <CheckCircle size={20} weight="fill" className="text-primary shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>

            <Button
              onClick={handleClaimPlatform}
              disabled={claiming || !selectedPlatformDomain}
              className="w-full h-12 rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 font-semibold text-base gap-2"
              data-testid="claim-platform-domain-btn"
            >
              {claiming ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Use {selectedPlatformDomain || '…'}
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* ── Free subdomain (linkflow.link) ── */}
        {mode === 'subdomain' && (
          <div className="bg-white rounded-2xl border border-border shadow-soft p-8 space-y-6">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                Your short link domain
              </label>
              <div className="flex items-center rounded-xl border border-border overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-shadow">
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(slugify(e.target.value))}
                  placeholder="myworkspace"
                  className="flex-1 h-12 px-4 text-base text-foreground placeholder:text-muted-foreground bg-white focus:outline-none"
                  data-testid="subdomain-input"
                />
                <span className="px-4 h-12 flex items-center bg-muted/50 text-sm font-medium text-muted-foreground border-l border-border whitespace-nowrap">
                  .{LINKFLOW_DOMAIN}
                </span>
              </div>

              <div className="mt-2 h-5">
                {checking && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                    Checking…
                  </span>
                )}
                {!checking && available === true && subdomain.length >= 3 && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1.5">
                    <CheckCircle size={13} weight="fill" />
                    <strong>{subdomain}.{LINKFLOW_DOMAIN}</strong> is available!
                  </span>
                )}
                {!checking && available === false && (
                  <span className="text-xs text-rose-600 flex items-center gap-1.5">
                    <XCircle size={13} weight="fill" />
                    That subdomain is taken or reserved. Try another.
                  </span>
                )}
              </div>
            </div>

            {subdomain && available === true && (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-1">
                  Your short links will look like
                </p>
                <p className="font-mono text-sm font-semibold text-foreground">
                  {subdomain}.{LINKFLOW_DOMAIN}/your-slug
                </p>
              </div>
            )}

            <Button
              onClick={handleClaimSubdomain}
              disabled={claiming || available !== true || subdomain.length < 3}
              className="w-full h-12 rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 font-semibold text-base gap-2"
              data-testid="claim-subdomain-btn"
            >
              {claiming ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Claim {subdomain || '…'}.{LINKFLOW_DOMAIN}
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* ── Custom domain ── */}
        {mode === 'custom' && (
          <div className="bg-white rounded-2xl border border-border shadow-soft p-8 space-y-6">
            <div className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground leading-relaxed">
              Connect a custom domain like{' '}
              <span className="font-mono text-foreground">go.yourcompany.com</span> in{' '}
              <strong>Settings → Domains</strong> after completing setup. DNS verification takes ~1 minute.
            </div>
            <Button
              onClick={handleSkip}
              className="w-full h-12 rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 font-semibold text-base gap-2"
            >
              Continue, I'll add my domain later
              <ArrowRight size={18} weight="bold" />
            </Button>
          </div>
        )}

        <button
          onClick={handleSkip}
          className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors mt-4"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
